import { useMemo } from "react";

import { useGetBillingConfig, useGetBillingShops, useGetMe } from "@/api";
import { PAY_DEBT_ROLE_NAMES, toAmount } from "@/lib/billing/constants";
import { useAuthStore } from "@/stores/auth.store";
import { useSessionStore } from "@/stores/session.store";

/** Commission state of the active store: debt, restriction, and online-payment readiness. */
export function useCommission() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const activeStoreId = useSessionStore((state) => state.activeStoreId);

  const { data: shopsData, isLoading } = useGetBillingShops({
    query: { enabled: isAuthenticated },
  });
  const { data: configData } = useGetBillingConfig({ query: { staleTime: Infinity } });
  const { data: meData } = useGetMe({ query: { enabled: isAuthenticated } });

  return useMemo(() => {
    const shops = shopsData?.status === 200 ? shopsData.data.data : [];
    const shop = shops.find((item) => item.store_id === activeStoreId);
    const config = configData?.status === 200 ? configData.data.data : undefined;
    const me = meData?.status === 200 ? meData.data.data : undefined;
    const roleName = me?.stores.find((store) => store.id === activeStoreId)?.role_name;

    return {
      isLoading,
      hasShop: Boolean(shop),
      outstandingDebt: toAmount(shop?.outstanding_debt),
      debtLimit: toAmount(config?.debt_limit),
      commissionRate: toAmount(config?.commission_rate ?? 0.04),
      isRestricted: shop?.is_restricted ?? false,
      onlinePaymentEnabled: shop?.online_payment_enabled ?? true,
      // Unknown role (e.g. platform admin): let the backend decide.
      canPayDebt:
        roleName === undefined ||
        (PAY_DEBT_ROLE_NAMES as readonly string[]).includes(roleName),
    };
  }, [shopsData, configData, meData, activeStoreId, isLoading]);
}
